import React from 'react';
import { Button } from '../ui/button';
import { Eye, Zap, PlayCircle, ArrowRight, Image, Layout, Palette } from 'lucide-react';
import PageLayout from '../layout/PageLayout';
import SEO from '../SEO';

const VisualTesting = ({ onBack, onLogin, onRegister }) => {
  return (
    <PageLayout onLogin={onLogin} onRegister={onRegister}>
      <SEO 
        title="Visual Testing - AI-Powered Visual Regression Testing | Lexa.run"
        description="Catch visual bugs before they reach production. AI-powered visual regression testing for pixel-perfect applications."
      />

      <main className="container mx-auto px-4 sm:px-6 py-12 pt-24">
        {/* Hero Section */}
        <div className="text-center mb-16 animate-fade-in-up">
          <div className="inline-flex items-center bg-pink-100 text-pink-600 px-4 py-2 rounded-full text-sm font-semibold mb-6">
            <Eye className="h-4 w-4 mr-2" />
            Visual Testing
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
            AI-Powered Visual Testing
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Detect visual regressions and UI inconsistencies automatically. Ensure pixel-perfect designs across all devices and browsers with AI-powered visual comparisons.
          </p>
          <Button onClick={onRegister} size="lg" className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white">
            <PlayCircle className="h-5 w-5 mr-2" />
            Start Testing Now
          </Button>
        </div>

        {/* Key Features */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-12">What You Can Test</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-pink-600 rounded-lg flex items-center justify-center mb-4">
                <Image className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3">Visual Regression</h3>
              <p className="text-gray-600">Automatically detect unintended visual changes by comparing screenshots across builds and releases.</p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center mb-4">
                <Layout className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3">Layout & Positioning</h3>
              <p className="text-gray-600">Verify element positions, spacing, alignment, and responsive layout behavior across screen sizes.</p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center mb-4">
                <Palette className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3">Colors & Fonts</h3>
              <p className="text-gray-600">Detect color discrepancies, font rendering issues, and style inconsistencies automatically.</p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center mb-4">
                <Eye className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3">Responsive Design</h3>
              <p className="text-gray-600">Test visual consistency across different viewports, devices, and screen resolutions simultaneously.</p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center mb-4">
                <Zap className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3">Dynamic Content</h3>
              <p className="text-gray-600">Handle animations, carousels, and dynamically loaded content with smart comparison algorithms.</p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-red-600 rounded-lg flex items-center justify-center mb-4">
                <Image className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3">Cross-Browser Testing</h3>
              <p className="text-gray-600">Compare visual rendering across Chrome, Firefox, Safari, and Edge to ensure consistency.</p>
            </div>
          </div>
        </div>

        {/* Visual Comparison Features */}
        <div className="bg-gradient-to-r from-pink-50 to-purple-50 rounded-2xl p-12 mb-16">
          <h2 className="text-3xl font-bold text-center mb-8">Smart Visual Comparison</h2>
          <p className="text-center text-gray-600 mb-8">AI-powered algorithms that understand your UI</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
            <div className="bg-white p-6 rounded-xl text-center shadow-md">
              <div className="text-4xl mb-3">🎯</div>
              <h4 className="font-bold">Pixel Precision</h4>
              <p className="text-sm text-gray-600">Pixel-level accuracy</p>
            </div>
            <div className="bg-white p-6 rounded-xl text-center shadow-md">
              <div className="text-4xl mb-3">🧠</div>
              <h4 className="font-bold">AI Filtering</h4>
              <p className="text-sm text-gray-600">Ignore expected changes</p>
            </div>
            <div className="bg-white p-6 rounded-xl text-center shadow-md">
              <div className="text-4xl mb-3">📐</div>
              <h4 className="font-bold">Layout Detection</h4>
              <p className="text-sm text-gray-600">Smart element tracking</p>
            </div>
            <div className="bg-white p-6 rounded-xl text-center shadow-md">
              <div className="text-4xl mb-3">⚡</div>
              <h4 className="font-bold">Fast Processing</h4>
              <p className="text-sm text-gray-600">Instant results</p>
            </div>
          </div>
        </div>

        {/* How It Works */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-12">How It Works</h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                1
              </div>
              <h3 className="text-xl font-bold mb-3">Capture Baseline</h3>
              <p className="text-gray-600">Take screenshots of your application in its correct state to establish visual baselines.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                2
              </div>
              <h3 className="text-xl font-bold mb-3">Run Comparison</h3>
              <p className="text-gray-600">AI compares new screenshots against baselines, highlighting differences and potential issues.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-green-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                3
              </div>
              <h3 className="text-xl font-bold mb-3">Review & Approve</h3>
              <p className="text-gray-600">Review visual diffs, approve intentional changes, and reject bugs with detailed reports.</p>
            </div>
          </div>
        </div>

        {/* Integrations Section - New Design */}
        <div className="mb-16 bg-white rounded-2xl p-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Column - Text */}
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Fits into your existing workflow
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                With comprehensive integrations for <span className="text-blue-600 font-semibold">CI/CD</span>, <span className="text-blue-600 font-semibold">issue tracking</span>, and <span className="text-blue-600 font-semibold">test case management</span>, Lexa fits into your current development and testing process with minimal disruption.
              </p>
            </div>

            {/* Right Column - Integration Logos */}
            <div>
              <div className="grid grid-cols-3 gap-4">
                {/* Row 1 */}
                <div className="bg-white border-2 border-gray-200 rounded-xl p-4 flex items-center justify-center hover:border-blue-300 hover:shadow-md transition-all">
                  <img src="https://customer-assets.emergentagent.com/job_smart-web-tester/artifacts/poehpjw0_image.png" alt="Azure DevOps" className="h-12 w-auto object-contain" />
                </div>
                <div className="bg-white border-2 border-gray-200 rounded-xl p-4 flex items-center justify-center hover:border-blue-300 hover:shadow-md transition-all">
                  <img src="https://upload.wikimedia.org/wikipedia/commons/3/32/Atlassian_Bitbucket_Logo.png" alt="Bitbucket" className="h-12 w-auto object-contain" />
                </div>
                <div className="bg-white border-2 border-gray-200 rounded-xl p-4 flex items-center justify-center hover:border-blue-300 hover:shadow-md transition-all">
                  <img src="https://customer-assets.emergentagent.com/job_smart-web-tester/artifacts/tdtq4iim_image.png" alt="CircleCI" className="h-12 w-auto object-contain" />
                </div>

                {/* Row 2 */}
                <div className="bg-white border-2 border-gray-200 rounded-xl p-4 flex items-center justify-center hover:border-blue-300 hover:shadow-md transition-all">
                  <img src="https://upload.wikimedia.org/wikipedia/commons/9/91/Octicons-mark-github.svg" alt="GitHub" className="h-12 w-auto object-contain" />
                </div>
                <div className="bg-white border-2 border-gray-200 rounded-xl p-4 flex items-center justify-center hover:border-blue-300 hover:shadow-md transition-all">
                  <img src="https://customer-assets.emergentagent.com/job_smart-web-tester/artifacts/lbn2nq9z_image.png" alt="Discord" className="h-12 w-auto object-contain" />
                </div>
                <div className="bg-white border-2 border-gray-200 rounded-xl p-4 flex items-center justify-center hover:border-blue-300 hover:shadow-md transition-all">
                  <img src="https://upload.wikimedia.org/wikipedia/commons/e/e1/GitLab_logo.svg" alt="GitLab" className="h-12 w-auto object-contain" />
                </div>

                {/* Row 3 */}
                <div className="bg-white border-2 border-gray-200 rounded-xl p-4 flex items-center justify-center hover:border-blue-300 hover:shadow-md transition-all">
                  <img src="https://customer-assets.emergentagent.com/job_smart-web-tester/artifacts/r25r80p5_image.png" alt="Jenkins" className="h-12 w-auto object-contain" />
                </div>
                <div className="bg-white border-2 border-gray-200 rounded-xl p-4 flex items-center justify-center hover:border-blue-300 hover:shadow-md transition-all">
                  <img src="https://customer-assets.emergentagent.com/job_smart-web-tester/artifacts/hymy3wvl_image.png" alt="TeamCity" className="h-12 w-auto object-contain" />
                </div>
                <div className="bg-white border-2 border-gray-200 rounded-xl p-4 flex items-center justify-center hover:border-blue-300 hover:shadow-md transition-all">
                  <img src="https://customer-assets.emergentagent.com/job_smart-web-tester/artifacts/mc6i5gez_image.png" alt="Jira" className="h-12 w-auto object-contain" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl p-12 text-center text-white">
          <h2 className="text-3xl font-bold mb-4">Ready to Catch Visual Bugs?</h2>
          <p className="text-xl mb-8 opacity-90">Start testing in minutes, not days. No credit card required.</p>
          <Button onClick={onRegister} size="lg" className="bg-white text-purple-600 hover:bg-gray-100">
            Start Free Trial
          </Button>
        </div>
      </main>
    </PageLayout>
  );
};

export default VisualTesting;
